import { createEntityAdapter, createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';

import { useOnboardingMessages } from '@/hooks/useOnboardingMessages';
import { threadApi } from '@/services/threadApi/threadApi';
import { ONBOARDING_THREAD_ID } from '@/utils/constants';
import { createTemporaryMessageId, isTemporaryMessageId } from '@/utils/temporaryUtils/temporaryIds';

import { setCurrentLayout } from '../layout/layoutSlice';
import { resetStep, Steps } from '../onboarding/onboardingSlice';
import { RootState } from '../store';
import { removeOnboardingThread, resetActiveThread } from '../thread/threadSlice';
import { TFollowUpsState, TMessage, TRole, TUpdateKpiDataVisualizationPayload } from './types';
import { findLastHumanMessageBefore } from './utils';

const messagesAdapter = createEntityAdapter<TMessage, string>({
  selectId: (message) => message.messageId,
  sortComparer: (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime(),
});

type ChatState = {
  isFetchingHistory: boolean;
  followUps: TFollowUpsState;
} & ReturnType<typeof messagesAdapter.getInitialState>;

const initialState: ChatState = {
  ...messagesAdapter.getInitialState(),
  isFetchingHistory: false,
  followUps: { threadId: null, items: [] },
};

const chatSlice = createSlice({
  name: 'chat',
  initialState,
  reducers: {
    addMessage: (state, action: PayloadAction<TMessage>) => {
      const messageId = action.payload?.messageId;
      const messageIdExists = Boolean(messageId);
      messagesAdapter.addOne(state, {
        ...action.payload,
        messageId: messageIdExists ? messageId : createTemporaryMessageId(),
      });
    },
    addManyMessages: (state, action: PayloadAction<TMessage[]>) => {
      const messagesWithIds = action.payload.map(
        (message): TMessage => ({
          ...message,
          messageId: message.messageId ?? createTemporaryMessageId(),
        }),
      );
      messagesAdapter.addMany(state, messagesWithIds);
    },
    clearManyMessages: (state, action: PayloadAction<{ idsToDelete: string[] }>) => {
      messagesAdapter.removeMany(state, action.payload.idsToDelete);
    },
    clearMessages: (state) => {
      messagesAdapter.removeAll(state);
    },
    addNextOnboardingQuestion: (state, action: PayloadAction<{ step: Steps; language: string }>) => {
      const { step, language } = action.payload;

      const onboardingMessages = useOnboardingMessages(language);

      const nextQuestion = onboardingMessages[step]?.messages?.content.find((message) => message.role === 'ai');
      if (nextQuestion) {
        const questionRecord = nextQuestion as unknown as Record<string, unknown>;
        const dataVisualisation = questionRecord?.data_visualization as TMessage['data_visualization'] | undefined;
        messagesAdapter.addOne(state, {
          role: nextQuestion.role as TRole,
          content: nextQuestion.content,
          data_visualization:
            dataVisualisation ?? ({ default_data_visualization: null, x_axis: null } as TMessage['data_visualization']),
          messageId: createTemporaryMessageId(),
          threadId: ONBOARDING_THREAD_ID,
          timestamp: new Date().toISOString(),
          isOnboardingQuestion: true,
          derivesFromPromptLibrary: false,
          userId: undefined,
        });
      }
    },
    updateMessageDataVisualization: (state, action: PayloadAction<TUpdateKpiDataVisualizationPayload>) => {
      messagesAdapter.updateOne(state, {
        id: action.payload.messageId,
        changes: { data_visualization: action.payload.data_visualization },
      });
    },
    upsertManyMessages: (state, action: PayloadAction<TMessage[]>) => {
      messagesAdapter.upsertMany(state, action.payload);
    },
    reconcileOptimisticMessage: (
      state,
      action: PayloadAction<{ optimisticMessageId: string; serverMessageId: string; threadId: string }>,
    ) => {
      const { optimisticMessageId, serverMessageId, threadId } = action.payload;
      const message = state.entities[optimisticMessageId];
      if (message) {
        messagesAdapter.removeOne(state, optimisticMessageId);
        messagesAdapter.addOne(state, { ...message, messageId: serverMessageId, threadId });
      }
    },
    clearFollowUps: (state) => {
      state.followUps = { threadId: null, items: [] };
    },
    setFollowUps: (state, action: PayloadAction<{ threadId: string; items: string[] }>) => {
      state.followUps = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(removeOnboardingThread, (state) => {
      const remainingMessages = state.ids.filter((id) => state.entities[id]?.threadId !== ONBOARDING_THREAD_ID);
      messagesAdapter.setAll(
        state,
        remainingMessages.map((id) => state.entities[id]!),
      );
    });
    builder.addCase(setCurrentLayout, (state, action) => {
      if (action.payload === 'onboardingMode') {
        messagesAdapter.removeAll(state);
      }
    });
    builder.addCase(resetStep, (state) => {
      const demoThreadId = state.ids.find((id) => {
        const message = state.entities[id];
        return message && message.threadId && message.threadId.startsWith('Demo');
      });

      if (demoThreadId) {
        const remainingMessages = state.ids.filter((id) => state.entities[id]?.threadId !== 'onboarding-thread');
        messagesAdapter.setAll(
          state,
          remainingMessages.map((id) => state.entities[id]!),
        );
      }
    });
    builder.addCase(resetActiveThread, (state) => {
      messagesAdapter.removeAll(state);
    });

    // INFO: Handles fetchMessagesByThreadId responses.
    // Replaces all messages in the store with the fetched messages for the thread.
    // Crucially, preserves optimistic messages (temp IDs) so they survive until sendMessage
    // completes and reconciles them with server IDs.
    builder.addMatcher(threadApi.endpoints.fetchMessagesByThreadId.matchFulfilled, (state, action) => {
      const fetchedThreadId = action?.meta?.arg?.originalArgs?.threadId;
      if (!fetchedThreadId) {
        state.isFetchingHistory = false;
        return;
      }

      // INFO: Keep optimistic messages for the fetched thread
      const optimisticMessages = state.ids
        .map((id) => state.entities[id])
        .filter(
          (msg): msg is TMessage =>
            msg != null && msg.threadId === fetchedThreadId && isTemporaryMessageId(msg.messageId),
        );

      // INFO: Replace all messages with optimistic messages + server messages
      messagesAdapter.setAll(state, [...optimisticMessages, ...action.payload]);

      state.isFetchingHistory = false;
    });
    builder.addMatcher(threadApi.endpoints.fetchMessagesByThreadId.matchPending, (state, action) => {
      const threadId = action.meta?.arg?.originalArgs?.threadId;
      const hasMessages = state.ids.some((id) => state.entities[id]?.threadId === threadId);
      if (!hasMessages) {
        state.isFetchingHistory = true;
      }
    });
    builder.addMatcher(threadApi.endpoints.fetchMessagesByThreadId.matchRejected, (state) => {
      state.isFetchingHistory = false;
    });
  },
});

export const {
  addMessage,
  addManyMessages,
  clearManyMessages,
  clearMessages,
  addNextOnboardingQuestion,
  updateMessageDataVisualization,
  upsertManyMessages,
  reconcileOptimisticMessage,
  clearFollowUps,
  setFollowUps,
} = chatSlice.actions;

export default chatSlice.reducer;

export const { selectAll: getAllMessages, selectById: getMessageById } = messagesAdapter.getSelectors(
  (state: RootState) => state.chat,
);

export const selectMessageById = (messageId: string) =>
  createSelector(
    (state: RootState) => state,
    (state) => getMessageById(state, messageId),
  );

export const selectLastUserMessageBefore = (messageId: string) =>
  createSelector(
    (state: RootState) => state,
    (state) => findLastHumanMessageBefore(state, { fromStore: { messageId } }),
  );

export const selectMessagesByThreadId = (threadId: string | null) => (state: RootState) => {
  const all = getAllMessages(state);
  return all
    .filter((message) => message.threadId === threadId)
    .sort((a, b) => {
      const dateDiff = new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime();
      if (dateDiff !== 0) return dateDiff;
      if (a.role === b.role) return 0;
      if (a.role === 'human') return -1;
      return 1;
    });
};

export const selectIsFetchingHistory = (state: RootState) => state.chat.isFetchingHistory;

export const selectFollowUps = (state: RootState) => {
  const { followUps } = state.chat;
  const activeThreadId = state.thread.activeThreadId;
  return followUps.threadId === activeThreadId ? followUps.items : [];
};
